import { IonActionSheet } from '@ionic/react';
import { type ReactNode, useState } from 'react';

import { useMemoizedFn, useRequest } from 'ahooks';
import { useModal } from '@ebay/nice-modal-react';
import AddModal, { type IAddModalProps } from '@/components/AddModal/index.jsx';
import { planDetailEvent } from '@/pages/PlanDetail/utils/event.js';
import { hooks } from '@/store.js';
import type { IListItem } from '@/pages/PlanDetail/interface.js';
import showErrMessage from '@/utils/showErrMessage.js';
import { toast } from 'sonner';
import useConfirm from './useConfirm/index.jsx';

export interface IUseCommonMenuProp {
  // onAddOpen(payload?: { isAppend?: boolean }): void;
  planId: string;

  id: string;
  getList: () => IListItem[] | undefined;
}

function findNextAvailable(arr: number[], initial: number): number {
  const numSet = new Set(arr); // 数组转Set提升查询效率
  let current = initial + 1; // 从初始值+1开始检测

  while (numSet.has(current)) {
    // 循环检测存在性
    current++;
  }
  return current;
}

function findPrevAvailable(arr: number[], initial: number): number {
  const numSet = new Set(arr); // 数组转Set提升查询效率
  let current = initial - 1; // 从初始值+1开始检测

  while (numSet.has(current)) {
    // 循环检测存在性
    current--;
  }
  return current;
}


const useCommonMenu = ({ planId, getList, id }: IUseCommonMenuProp) => {
  const [placeholder, setPlaceholder] = useState<ReactNode>();
  const addModal = useModal(AddModal);
  const client = hooks.useClient();

  const { loading: deleting,  runAsync: runDelete } = useRequest(
		async () => {
      const eventId = id;
			if (!eventId) throw new Error("未传入id");

			return await client.events.delete(eventId);
		},
		{
			manual: true,
			onError: showErrMessage,
			onSuccess() {
        toast.success("删除成功");
				setTimeout(() => {
					planDetailEvent.emit("requestRefresh");
				}, 1000);

			
			},
		},
	);

  const { showModal } = useConfirm({
    isSubmitting: deleting,
    onSubmit: runDelete,
    title: "确定删除该活动吗?",
  });

  
  const show = useMemoizedFn(() => {
    setPlaceholder(
      <IonActionSheet
        isOpen
        onDidDismiss={(e) => {
          setPlaceholder(null);
        }}
        buttons={[
          {
            text: '向前插入',
            handler() {
              const list = getList();
              if (!list) return;
              const orderIdxList = list
                .map((i) => i.orderIdx)
                .filter((i) => i) as number[];

              client.events.get(id).resolved.then((res) => {
                const date = res?.get('date');
                const orderIdx = res?.get('orderIdx');
                if (!date || !orderIdx) return;

                const nextOrderIdx = findPrevAvailable(orderIdxList, orderIdx);

                addModal.show({
                  planId,
                  date,
                  onSuccess: () => {
                    planDetailEvent.emit('requestRefresh');
                  },
                  orderIdx: nextOrderIdx,
                } satisfies IAddModalProps);
              });
            },
          },
          {
            text: '向后插入',
            handler() {
              const list = getList();
              if (!list) return;
              const orderIdxList = list
                .map((i) => i.orderIdx)
                .filter((i) => i) as number[];

              client.events.get(id).resolved.then((res) => {
                const date = res?.get('date');
                const orderIdx = res?.get('orderIdx');
                if (!date || !orderIdx) return;

                const nextOrderIdx = findNextAvailable(orderIdxList, orderIdx);

                addModal.show({
                  planId,
                  date,
                  onSuccess: () => {
                    planDetailEvent.emit('requestRefresh');
                  },
                  orderIdx: nextOrderIdx,
                } satisfies IAddModalProps);
              });
            },
          },
          {
            text: '上移',
            handler() {
              planDetailEvent.emit('reorder', 'up', id);
            },
          },
          {
            text: '下移',
            handler() {
              planDetailEvent.emit('reorder', 'down', id);
            },
          },
          {
            text: '置顶',
            handler() {
              planDetailEvent.emit('reorder', 'start', id);
            },
          },
          {
            text: '置底',
            handler() {
              planDetailEvent.emit('reorder', 'end', id);
            },
          },
          {
            text: '删除',
            handler() {
              showModal()
            },
          },

          {
            text: 'Cancel',
            role: 'cancel',
            data: {
              action: 'cancel',
            },
          },
        ]}
      />
    );
  });

  return {
    show,
    placeholder,
  };
};

export default useCommonMenu;
